/**
 * T114: Variable Input Form
 * Dynamic form for filling in template variables
 */

import { useForm } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { VariableInfo } from '@/services/prompts'

interface VariableInputFormProps {
  variables: VariableInfo[]
  onSubmit: (values: Record<string, string>) => void
  isLoading?: boolean
}

export function VariableInputForm({
  variables,
  onSubmit,
  isLoading,
}: VariableInputFormProps) {
  const defaultValues = variables.reduce(
    (acc, v) => ({
      ...acc,
      [v.name]: v.default_value ?? '',
    }),
    {} as Record<string, string>
  )

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues,
  })

  const handleFormSubmit = (data: Record<string, string>) => {
    onSubmit(data)
  }

  if (variables.length === 0) {
    return (
      <Card>
        <CardContent className="py-6">
          <p className="text-sm text-muted-foreground">
            This prompt has no variables to fill in.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Variables</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {variables.map((variable) => (
            <div key={variable.name} className="grid gap-2">
              <Label htmlFor={variable.name}>
                {variable.name}
                {variable.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              <Input
                id={variable.name}
                {...register(variable.name, {
                  required: variable.required ? `${variable.name} is required` : false,
                })}
                placeholder={variable.default_value ?? `Enter ${variable.name}...`}
              />
              {errors[variable.name] && (
                <p className="text-sm text-destructive">
                  {errors[variable.name]?.message as string}
                </p>
              )}
            </div>
          ))}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Generating...' : 'Generate'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
